'use client';

import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaPosterImage,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react';
import {
  MediaRenditionMenu,
  MediaSettingsMenuButton,
  MediaSettingsMenu,
  MediaSettingsMenuItem,
  MediaAudioTrackMenu,
  MediaCaptionsMenu,
} from 'media-chrome/react/menu';
import { useLocale, useTranslations } from 'next-intl';
import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import VimeoVideo from 'vimeo-video-element/react';
import YoutubeVideo from 'youtube-video-element/react';

import {
  Button,
  HeroIcon,
  PlayerCastButton,
  PlayerSeekBackwardButton,
  PlayerSeekForwardButton,
  Typography,
} from '@/components/atoms';
import { PlayAuthButton } from '@/components/atoms/PlayerControl/PlayAuthButton';
import {
  defaultButtonStyling,
  defaultDisplayStyling,
  defaultMenuStyling,
  defaultRangeStyling,
} from '@/components/atoms/PlayerControl/util';
import { LikeButton } from '@/components/molecules';
import { useAnalytics } from '@/lib/contexts/analytics';
import { useAuthContext } from '@/lib/contexts/auth/auth-provider';
import { useCanAccess } from '@/lib/hooks/auth';
import { useDialog } from '@/lib/hooks/auth/useDialog';
import { useMediaController } from '@/lib/hooks/media-controller/useMediaController';
import {
  refreshVideoTokensWithRetry,
  setTokenInUrl,
  type RefreshVideoTokensResult,
} from '@/lib/player/tokens';
import { getPackageInfo } from '@/lib/utils/getPackageInfo';
import { resolveYouTubeUrl } from '@/lib/utils/resolve-youtube-url';
import { cn } from '@/lib/utils/tailwind';
import {
  Duration,
  durationToTimecode,
  secondsToDuration,
} from '@/lib/utils/time-formatting';

import styles from './VideoPlayer.module.css';

import '@mux/mux-video';

import type { Security, Video, PlayerType } from '@/lib/api';
import type { SendStreamMarkerType } from '@/lib/contexts/analytics';
import type { AccessContentPayload } from '@/lib/utils/access';

import type { VimeoVideoElement } from '@/lib/player/types';
import type { UUID } from '@/types';

const ENABLE_TOKEN_LOADING_OVERLAY = true;

interface PositionMarkers {
  start: number;
  end: number;
}

type Mode = 'feature' | 'preview';
type VideoPlayerType = PlayerType | 'file';

interface Props {
  ref?: RefObject<HTMLVideoElement | null>;
  tenantId?: string;
  type: VideoPlayerType;
  mode?: Mode;
  trackId?: string | number;
  title: string;
  videoUrl: string;
  security?: Security;
  access?: AccessContentPayload;
  posterImageUrl?: string;
  liked?: boolean;
  muted?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  onEnded?: () => void;
  onFatalError?: (error: Error) => void;
}

export function VideoPlayer({
  ref,
  tenantId,
  type,
  mode = 'feature',
  trackId,
  title,
  videoUrl,
  security,
  access,
  posterImageUrl,
  muted = false,
  autoPlay = false,
  loop = false,
  onEnded = () => {},
  onFatalError = () => {},
}: Props) {
  const t = useTranslations();
  const [initialized, setInitialized] = useState<boolean>(mode === 'preview');
  const [duration, setDuration] = useState<Duration>({
    seconds: 0,
    minutes: 0,
  });
  const [positionMarkers, setPositionMarkers] = useState<PositionMarkers>({
    start: 0,
    end: 0,
  });
  const [isTokenLoading, setIsTokenLoading] = useState<boolean>(false);
  const [videoTokens, setVideoTokens] =
    useState<RefreshVideoTokensResult | null>(security ?? null);
  const videoRef = useRef<HTMLVideoElement | VimeoVideoElement>(null);
  const mediaControllerRef = useRef<HTMLElement>(null);
  const currentVideoHeight = useRef<number>(null);
  const cumulativeTimeRef = useRef<number>(0);
  const lastPlayMarkerRef = useRef<number>(null);
  const intervalSentRef = useRef<boolean>(false);
  const seekDebouncerRef = useRef<NodeJS.Timeout>(null);
  const isScrubbingRef = useRef<boolean>(false);
  const milestonesHitRef = useRef<Set<number>>(new Set());
  const playTriggerRef = useRef<{ link_id: string; link_text: string }>({
    link_id: '',
    link_text: '',
  });
  const isRecoveringRef = useRef<boolean>(false);
  const recoveryAttemptsRef = useRef<number>(0);
  const tokenLoadInitiatedRef = useRef<boolean>(false);
  const playAttemptRef = useRef<number>(0);
  const MAX_RECOVERY_ATTEMPTS = 1;

  const resetRecoveryState = useCallback(() => {
    recoveryAttemptsRef.current = 0;
    isRecoveringRef.current = false;
  }, []);

  useEffect(() => {
    const patchRemoveChild = (
      element: HTMLElement | null,
    ): (() => void) | null => {
      if (!element) return null;
      const originalRemoveChild = element.removeChild.bind(element);
      (element as HTMLElement).removeChild = <T extends Node>(node: T) => {
        if (node.parentNode === element) {
          return originalRemoveChild(node) as T;
        }
        return node;
      };
      return () => {
        element.removeChild = originalRemoveChild;
      };
    };

    const cleanups = [
      patchRemoveChild(mediaControllerRef.current),
      patchRemoveChild(videoRef.current as unknown as HTMLElement),
    ];

    return () => {
      cleanups.forEach(cleanup => cleanup?.());
    };
  }, []);

  const { isAuthenticated, getUserDataField } = useAuthContext();
  const newUser = getUserDataField(u => u.new);
  const customerType = !isAuthenticated
    ? 'undefined'
    : !newUser
      ? 'returning'
      : 'new';

  const acl = useCanAccess(access as unknown as Video);
  const aclRef = useRef<string>(acl);

  useEffect(() => {
    aclRef.current = acl;
  }, [acl]);

  const { openDialog } = useDialog();
  const locale = useLocale();
  const { pauseMiniPlayer, resumeMiniPlayer } = useMediaController();
  const { getSessionId, generateStreamId, track, getMuxDataKey } =
    useAnalytics();

  const [streamId, setStreamId] = useState<UUID>(generateStreamId());
  const previousAclRef = useRef<string>(acl);
  const tokenRefreshPromiseRef =
    useRef<Promise<RefreshVideoTokensResult> | null>(null);

  const refreshTokensShared = useCallback(
    async (retries = 0): Promise<RefreshVideoTokensResult> => {
      if (!trackId) {
        return Promise.reject(new Error('Missing video track id'));
      }

      if (tokenRefreshPromiseRef.current) {
        return tokenRefreshPromiseRef.current;
      }

      const promise = refreshVideoTokensWithRetry(
        trackId,
        locale,
        retries,
      ).finally(() => {
        tokenRefreshPromiseRef.current = null;
      });
      tokenRefreshPromiseRef.current = promise;
      return promise;
    },
    [trackId, locale],
  );

  useEffect(() => {
    const wasNotGranted =
      previousAclRef.current === 'unauthenticated' ||
      previousAclRef.current === 'unauthorized';
    const isNowGranted = acl === 'granted';

    if (wasNotGranted && isNowGranted) {
      const newStreamId = generateStreamId();
      setStreamId(newStreamId);
      setInitialized(mode === 'preview');
      cumulativeTimeRef.current = 0;
      lastPlayMarkerRef.current = null;
      intervalSentRef.current = false;
      milestonesHitRef.current = new Set();
      playTriggerRef.current = { link_id: '', link_text: '' };

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.load();
      }
    }

    previousAclRef.current = acl;
  }, [acl, generateStreamId, mode]);

  const sendStreamBeacon = useCallback(
    (event: SendStreamMarkerType) => {
      if (!streamId || !positionMarkers || mode === 'preview') return;
      if (aclRef.current !== 'granted') return;

      const time = lastPlayMarkerRef.current
        ? Date.now() - lastPlayMarkerRef.current
        : 0;
      cumulativeTimeRef.current += time;
      lastPlayMarkerRef.current =
        event === 'interval' || event === 'rendition_change'
          ? Date.now()
          : null;

      const rendition = currentVideoHeight.current
        ? `${currentVideoHeight.current}p`
        : 'unknown';

      const { end } = positionMarkers;
      const timer = {
        start: end,
        end: videoRef.current?.currentTime ?? -1,
      };

      if (event === 'seeked' && timer.start === timer.end) return;

      track({
        type: 'stream_marker',
        streamId,
        markerType: event,
        timer,
        cumulativeTime: Math.round(cumulativeTimeRef.current / 1000),
        rendition,
      });

      setPositionMarkers(timer);
    },
    [positionMarkers, streamId, mode, track],
  );

  const aclCheckInternal = (currentAcl: string, showModal: boolean = false) => {
    if (!videoRef.current) return true;

    const allowed = mode !== 'preview' && currentAcl === 'granted';
    if (!allowed && showModal) {
      videoRef.current.pause();
      openDialog?.('auth');
    }

    return allowed;
  };

  const aclCheck = useCallback(
    (showModal: boolean = false) => {
      if (!videoRef.current) return true;

      const allowed = mode !== 'preview' && aclRef.current === 'granted';

      if (!allowed && showModal) {
        videoRef.current.pause();
        openDialog?.('auth');
      }

      return allowed;
    },
    [mode, openDialog],
  );

  const initialize = useCallback(() => {
    if (initialized) {
      return;
    }

    if (aclRef.current !== 'granted') {
      return;
    }
    if (!aclCheck()) {
      return;
    }

    const rendition = currentVideoHeight.current
      ? `${currentVideoHeight.current}p`
      : 'unknown';

    setInitialized(true);

    track({
      type: 'stream_init',
      streamId,
      contentPlayed: {
        id: (trackId || '').toString(),
        type: 'video',
        rendition,
      },
      contentParent: {
        id: null,
        type: null,
      },
    });
  }, [initialized, streamId, trackId, aclCheck, track]);

  const handleOnResize = useCallback(
    (e: Event) => {
      // @ts-expect-error - videoHeight is not exported as a type
      currentVideoHeight.current = e.target?.videoHeight;

      if (initialized) {
        sendStreamBeacon('rendition_change');
      }
    },
    [initialized, sendStreamBeacon],
  );

  const loadVideoSource = useCallback(
    (tokens: { playback_token: string; timeline_token?: string }) => {
      const video = videoRef.current;
      if (!video) return;

      const newSrc =
        type === 'mux'
          ? setTokenInUrl(
              `https://stream.mux.com/${videoUrl}.m3u8`,
              tokens.playback_token,
            )
          : setTokenInUrl(videoUrl, tokens.playback_token);
      video.setAttribute('src', newSrc);
      video.setAttribute('cast-src', newSrc);

      const storyboardTrack = video.querySelector<HTMLTrackElement>(
        'track[kind="metadata"]',
      );
      if (storyboardTrack && tokens.timeline_token) {
        storyboardTrack.src = setTokenInUrl(
          `https://image.mux.com/${videoUrl}/storyboard.vtt`,
          tokens.timeline_token,
        );
      }
    },
    [type, videoUrl],
  );

  const waitForLoadedMetadata = (video: HTMLVideoElement): Promise<void> =>
    new Promise((resolve, reject) => {
      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        resolve();
        return;
      }

      const onLoadedMetadata = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = window.setTimeout(() => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        reject(new Error('Video load timeout'));
      }, 10_000);

      video.addEventListener('loadedmetadata', onLoadedMetadata, {
        once: true,
      });
    });

  const resumePlayback = useCallback(
    async (
      tokens: RefreshVideoTokensResult,
      resumeTime?: number,
    ): Promise<void> => {
      const attemptId = (playAttemptRef.current += 1);
      const video = videoRef.current;
      if (!video) {
        throw new Error('Video playback failed');
      }

      loadVideoSource(tokens);

      try {
        // Safari requires play() to be invoked while still inside a user
        // gesture. Calling waitForLoadedMetadata() before play() lets the
        // gesture expire and, for HLS streams, Safari may never reach
        // HAVE_CURRENT_DATA until a play request has been made. Start playback
        // immediately and only wait for loadedmetadata if the first request is
        // aborted.
        if (resumeTime && resumeTime > 0) {
          video.currentTime = resumeTime;
        }
        await video.play();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        if (error.name === 'AbortError') {
          await waitForLoadedMetadata(video);
          if (attemptId !== playAttemptRef.current) {
            return;
          }
          if (resumeTime && resumeTime > 0) {
            video.currentTime = resumeTime;
          }
          await video.play();
        } else {
          throw error;
        }
      }
    },
    [loadVideoSource],
  );

  const handleFirstPlay = useCallback(async () => {
    if (!videoRef.current || !aclCheck(true)) {
      return;
    }

    if (tokenLoadInitiatedRef.current) return;
    tokenLoadInitiatedRef.current = true;

    playTriggerRef.current = {
      link_id: 'video-play-button',
      link_text: 'Play',
    };

    const tokens = videoTokens ?? security;
    if (tokens?.playback_token) {
      try {
        resetRecoveryState();
        await resumePlayback(tokens);
        if (mode === 'feature') {
          pauseMiniPlayer(true);
        }
        return;
      } catch {
        tokenLoadInitiatedRef.current = false;
        onFatalError(new Error('Video playback failed'));
        return;
      }
    }

    tokenLoadInitiatedRef.current = false;
    onFatalError(new Error('Video playback failed'));
  }, [
    aclCheck,
    mode,
    onFatalError,
    pauseMiniPlayer,
    resetRecoveryState,
    security,
    resumePlayback,
    videoTokens,
  ]);

  const handlePlayPauseClick = useCallback(
    (event: React.MouseEvent) => {
      const video = videoRef.current;

      if (!video || !aclCheck(true)) {
        return;
      }

      // Only intercept the first play click. After initialization the source is
      // already loaded, so let MediaPlayButton toggle play/pause natively.
      // Calling play()/pause() ourselves collides with MediaPlayButton's bubble
      // handler and can cause Chrome to restart playback on pause.
      if (initialized) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      playTriggerRef.current = {
        link_id: 'video-play-button',
        link_text: 'Play',
      };

      const tokens = videoTokens ?? security;
      if (tokens?.playback_token) {
        resetRecoveryState();
        resumePlayback(tokens).catch(() => {
          onFatalError(new Error('Video playback failed'));
        });
        if (mode === 'feature') {
          pauseMiniPlayer(true);
        }
        return;
      }

      onFatalError(new Error('Video playback failed'));
    },
    [
      aclCheck,
      initialized,
      mode,
      onFatalError,
      pauseMiniPlayer,
      resetRecoveryState,
      security,
      resumePlayback,
      videoTokens,
    ],
  );

  const handleOnPlay = useCallback(async () => {
    if (!videoRef.current) {
      return;
    }
    if (!aclCheck(true)) {
      videoRef.current.pause();
      return;
    }
    if (!playTriggerRef.current.link_id) {
      playTriggerRef.current = {
        link_id: 'video-play-button',
        link_text: '',
      };
    }

    if (!initialized && (type === 'mux' || type === 'hls')) {
      if (tokenLoadInitiatedRef.current) return;
      tokenLoadInitiatedRef.current = true;

      const tokens = videoTokens ?? security;
      if (tokens?.playback_token) {
        resetRecoveryState();
        resumePlayback(tokens).catch(() => {
          onFatalError(new Error('Video playback failed'));
        });
        return;
      }

      onFatalError(new Error('Video playback failed'));
      return;
    }

    if (type === 'youtube' && aclCheck()) {
      initialize();
    }

    if (mode === 'feature') {
      pauseMiniPlayer(true);
    }
  }, [
    aclCheck,
    initialize,
    initialized,
    onFatalError,
    pauseMiniPlayer,
    security,
    resumePlayback,
    resetRecoveryState,
    trackId,
    type,
    videoTokens,
    mode,
  ]);

  const handleOnPlaying = useCallback(() => {
    if (!aclCheck(true)) {
      return;
    }

    lastPlayMarkerRef.current = Date.now();
    recoveryAttemptsRef.current = 0;
    isRecoveringRef.current = false;
  }, [aclCheck]);

  const handleOnPause = useCallback(
    () => initialized && sendStreamBeacon('pause'),
    [initialized, sendStreamBeacon],
  );

  const handleOnSeeked = useCallback(() => {
    if (isScrubbingRef.current) return;
    clearTimeout(seekDebouncerRef.current || undefined);
    seekDebouncerRef.current = setTimeout(
      () => sendStreamBeacon('seeked'),
      100,
    );
  }, [sendStreamBeacon]);

  const handleOnError = useCallback(async () => {
    sendStreamBeacon('error');

    const video = videoRef.current;

    if (!video || !trackId || (type !== 'mux' && type !== 'hls')) {
      onFatalError(new Error('Video playback failed'));
      return;
    }

    if (isRecoveringRef.current) {
      return;
    }

    if (recoveryAttemptsRef.current >= MAX_RECOVERY_ATTEMPTS) {
      isRecoveringRef.current = true;
      onFatalError(new Error('Video playback failed'));
      return;
    }

    isRecoveringRef.current = true;
    setIsTokenLoading(true);
    recoveryAttemptsRef.current += 1;

    const resumeTime = video.currentTime;

    try {
      const freshTokens = await refreshTokensShared(0);
      setVideoTokens(freshTokens);
      await resumePlayback(freshTokens, resumeTime);
    } catch {
      if (security?.playback_token) {
        try {
          await resumePlayback(security, resumeTime);
        } catch {
          onFatalError(new Error('Video playback failed'));
        }
      } else {
        onFatalError(new Error('Video playback failed'));
      }
    } finally {
      setIsTokenLoading(false);
    }
  }, [
    sendStreamBeacon,
    onFatalError,
    type,
    trackId,
    resumePlayback,
    security,
    refreshTokensShared,
  ]);

  const handleOnLoadedMetadata = useCallback(
    (event: Event) => {
      const target = event.currentTarget as HTMLVideoElement;
      const duration = secondsToDuration(target.duration);
      setDuration(duration);
      if (type === 'youtube') return;
      if (!aclCheck()) return;
      initialize();
    },
    [type, initialize, aclCheck],
  );

  const handleOnEnded = useCallback(() => {
    onEnded();
    sendStreamBeacon('ended');

    if (mode === 'preview') return;

    resumeMiniPlayer(true);
  }, [mode, onEnded, resumeMiniPlayer, sendStreamBeacon]);

  const handleOnTimeUpdate = useCallback(() => {
    if (!aclCheck(true)) return;
    if (!lastPlayMarkerRef.current) return;

    const virtualCumulativeTime =
      cumulativeTimeRef.current + (Date.now() - lastPlayMarkerRef.current);

    if (!intervalSentRef.current && virtualCumulativeTime > 30000) {
      intervalSentRef.current = true;
      sendStreamBeacon('interval');
    }

    const video = videoRef.current;
    if (!video || !video.duration) return;

    const pct = virtualCumulativeTime / (video.duration * 1000);
    const sessionId = getSessionId();

    const milestoneEventName: Record<number, string> = {
      0.25: 'video_viewed_25',
      0.5: 'video_viewed_50',
      0.75: 'video_viewed_75',
    };

    for (const threshold of [0.25, 0.5, 0.75]) {
      if (pct >= threshold && !milestonesHitRef.current.has(threshold)) {
        milestonesHitRef.current.add(threshold);
        track({
          type: 'custom',
          eventName: milestoneEventName[threshold],
          eventData: {
            content_type: 'video',
            video_attention: Math.round(pct * 100) / 100,
            locale,
            stream_id: streamId,
            session_id: sessionId,
            content_id: (trackId || '').toString(),
            customer_type: customerType,
            link_id: playTriggerRef.current.link_id,
            link_text: playTriggerRef.current.link_text,
          },
        });
      }
    }
  }, [
    sendStreamBeacon,
    aclCheck,
    getSessionId,
    track,
    streamId,
    trackId,
    locale,
    customerType,
  ]);

  useEffect(() => {
    return () => {
      if (!initialized || mode === 'preview') return;
      resumeMiniPlayer(true);
    };
  }, [resumeMiniPlayer, initialized, mode]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const listeners = [
      { event: 'play', handler: handleOnPlay },
      { event: 'playing', handler: handleOnPlaying },
      { event: 'pause', handler: handleOnPause },
      { event: 'seeked', handler: handleOnSeeked },
      { event: 'resize', handler: handleOnResize },
      { event: 'loadedmetadata', handler: handleOnLoadedMetadata },
      { event: 'ended', handler: handleOnEnded },
      { event: 'timeupdate', handler: handleOnTimeUpdate },
      { event: 'error', handler: handleOnError },
    ];

    listeners.forEach(({ event, handler }) => {
      videoRef.current?.removeEventListener(event, handler as EventListener);
      videoRef.current?.addEventListener(event, handler as EventListener);
    });
  }, [
    acl,
    handleOnPlay,
    handleOnPlaying,
    handleOnPause,
    handleOnSeeked,
    handleOnResize,
    handleOnLoadedMetadata,
    handleOnEnded,
    handleOnTimeUpdate,
    handleOnError,
  ]);

  const resolvePlayer = () => {
    const style = {
      '--media-object-fit': mode === 'preview' ? 'cover' : 'contain',
    } as CSSProperties;
    const props: Record<string, unknown> = {
      ref: ref || videoRef,
      className: 'aspect-video',
      style,
      streamType: 'vod',
      slot: 'media',
      preload: 'metadata',
      tabIndex: -1,
      rel: '0',
      autoPlay,
      muted,
      loop,
      playsInline: true,
      controls: false,
      onplay: handleOnPlay,
      onplaying: handleOnPlaying,
      onpause: handleOnPause,
      onseeked: handleOnSeeked,
      onresize: handleOnResize,
      onloadedmetadata: handleOnLoadedMetadata,
      onended: handleOnEnded,
      ontimeupdate: handleOnTimeUpdate,
      onerror: handleOnError,
    };

    // Simon sez it's okay to do this for both feature and preview
    if (mode === 'feature' || mode === 'preview') {
      props['env-key'] = getMuxDataKey();
      props['metadata-video-id'] = mode === 'feature' ? trackId : videoUrl;
      props['metadata-video-title'] = title;
      props['metadata-video-client-application-name'] = 'web-app';
      props['metadata-video-client-application-version'] =
        getPackageInfo().version;
      props['metadata-video-content-type'] = mode;
      props['metadata-video-brand'] = tenantId;
      props['metadata-view-session-id'] = getSessionId();
      props['metadata-player-instance-id'] = streamId;
    }

    switch (type) {
      case 'file': {
        const nativeVideoRef = (ref || videoRef) as RefObject<HTMLVideoElement>;

        return (
          <video
            ref={nativeVideoRef}
            className="aspect-video"
            style={style}
            slot="media"
            src={videoUrl}
            preload="metadata"
            tabIndex={-1}
            autoPlay={autoPlay}
            muted={muted}
            loop={loop}
            playsInline
          >
            <track kind="captions" />
          </video>
        );
      }
      case 'hls': {
        return (
          // @ts-expect-error: In order to support renditions, we need ot use web components
          <mux-video {...props} crossOrigin="anonymous" />
        );
      }
      case 'mux': {
        return (
          // @ts-expect-error: In order to support renditions, we need ot use web components
          <mux-video {...props} crossOrigin="anonymous">
            {videoUrl && (
              <track
                label="thumbnails"
                default
                kind="metadata"
                src={buildUrlWithQuery(
                  `https://image.mux.com/${videoUrl}/storyboard.vtt`,
                  security?.timeline_token,
                )}
              />
            )}
            {/* @ts-expect-error: In order to support renditions, we need ot use web components */}
          </mux-video>
        );
      }
      case 'vimeo': {
        return (
          <VimeoVideo {...props} config={{ chromecast: true, airplay: true }} />
        );
      }
      case 'youtube': {
        props.src = resolveYouTubeUrl(videoUrl);

        return <YoutubeVideo {...props} />;
      }
      default: {
        const error = new Error('Unknown player type provided');
        onFatalError(error);
        throw error;
      }
    }
  };

  const durationDisplay = durationToTimecode(duration);

  return (
    <MediaController
      ref={node => {
        mediaControllerRef.current = node as HTMLElement | null;
      }}
      className="flex w-full h-full max-h-[-webkit-fill-available] pb-px"
      defaultSubtitles={false}
      gesturesDisabled={mode === 'preview' || !aclCheckInternal(acl)}
      lang={locale}
    >
      {!aclCheckInternal(acl) && <div className="absolute inset-0" />}
      {resolvePlayer()}
      {ENABLE_TOKEN_LOADING_OVERLAY && isTokenLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-2 text-(--color-neutral-25)">
            <HeroIcon icon={ArrowPathIcon} className="h-8 w-8 animate-spin" />
            <Typography.Text className="text-body-md">
              {t('BUTTON_TITLE_LOADING')}
            </Typography.Text>
          </div>
        </div>
      )}
      <div
        className={clsx({
          'absolute top-0 right-0 bottom-0 left-0 pointer-events-none px-4 pt-4 pb-6 sm:pb-8 md:pb-24 lg:pb-36 sm:p-36 text-border-neutral-softer-3 bg-gradient-hero':
            mode === 'feature',
          hidden: mode === 'preview',
        })}
      >
        <div
          className={clsx({
            'sr-only': initialized,
            'flex items-end w-full h-full': !initialized,
          })}
        >
          <div>
            <div>
              <Typography.Text
                className={cn([
                  'hero-text-display',
                  'px-2',
                  'py-1',
                  'w-fit',
                  'hero-text-style',
                  'bg-(--hero-text-background)',
                  'text-shadow-(--hero-text-shadow)',
                  'text-(--hero-text-color)',
                  'font-(family-name:--font-body)',
                  'text-(length:--text-body-md)',
                  'font-(--font-weight-body-md-bold)',
                ])}
              >
                {t('CONTENT_TYPE_VIDEO')}
              </Typography.Text>
            </div>
            <div className={cn(['pt-[0.9rem]'])}>
              <Typography.Heading
                variant="h1"
                className={cn([
                  'px-2',
                  'leading-none!',
                  'hero-text-display',
                  'hero-box-decoration',
                  'bg-(--hero-text-background)',
                  'text-(--hero-text-color)',
                  'text-shadow-(--hero-text-shadow)',
                  'max-w-[25ch]',
                  'text-balance',
                  'text-(length:--text-h6)',
                  'md:text-(length:--text-h5)',
                  'font-(--font-weight-h6)',
                ])}
              >
                {title}
              </Typography.Heading>
            </div>
          </div>
        </div>
      </div>
      <span
        className={clsx({
          hidden: mode === 'preview',
          'z-1': true,
        })}
        slot="centered-chrome"
      >
        <span className="text-(--color-neutral-25)">
          <div className="flex items-center gap-(--horizontal-spacing-1)">
            <div
              className={clsx({
                hidden: !initialized,
                'md:hidden': true,
              })}
            >
              <PlayerSeekBackwardButton disabled={!aclCheckInternal(acl)} />
            </div>
            <PlayAuthButton
              className={clsx({ hidden: aclCheckInternal(acl) })}
              disabled={aclCheckInternal(acl)}
              onClick={() => {
                playTriggerRef.current = {
                  link_id: 'video-play-auth-button',
                  link_text: 'Play',
                };
                aclCheck(true);
              }}
            />
            {initialized ? (
              <>
                {/* MediaPlayButton toggles playback internally on the bubble
                    phase. This capture-phase wrapper lets us intercept the
                    click first, use the cached token, and prevent the native
                    toggle from racing ahead of our token handling. */}
                <span onClickCapture={handlePlayPauseClick}>
                  <MediaPlayButton
                    className={clsx({
                      hidden: !aclCheckInternal(acl),
                      'md:hidden': true,
                    })}
                    style={defaultButtonStyling}
                    disabled={!aclCheckInternal(acl)}
                  >
                    <span slot="play">
                      <Button aria-hidden="true" tabIndex={-1} size="sm">
                        <HeroIcon icon={PlayIcon} />
                      </Button>
                    </span>
                    <span slot="pause">
                      <Button aria-hidden="true" tabIndex={-1} size="sm">
                        <HeroIcon icon={PauseIcon} />
                      </Button>
                    </span>
                  </MediaPlayButton>
                </span>
              </>
            ) : (
              <Button
                className={clsx({
                  hidden: !aclCheckInternal(acl),
                })}
                style={defaultButtonStyling}
                size="sm"
                disabled={false}
                onClick={handleFirstPlay}
                aria-label={t('FOCAL_PLAY')}
              >
                <HeroIcon icon={PlayIcon} />
              </Button>
            )}
            <div
              className={clsx({
                hidden: !initialized,
                'md:hidden': true,
              })}
            >
              <PlayerSeekForwardButton disabled={!aclCheckInternal(acl)} />
            </div>
          </div>
        </span>
      </span>
      <MediaPosterImage
        slot="poster"
        src={posterImageUrl}
        style={
          {
            '--media-object-fit': 'cover',
          } as CSSProperties
        }
      />
      <MediaSettingsMenu
        className="z-2 sm:py-(--player-menu-item-vspacing-sm) md:py-(--player-menu-item-vspacing-md)"
        anchor="auto"
        style={defaultMenuStyling}
        hidden
      >
        <MediaSettingsMenuItem className="py-[.4em] px-[.8em]">
          {t('PLAYER_SETTINGS_AUDIO')}
          <MediaAudioTrackMenu slot="submenu" hidden>
            <div slot="title">{t('PLAYER_SETTINGS_AUDIO')}</div>
          </MediaAudioTrackMenu>
        </MediaSettingsMenuItem>
        <MediaSettingsMenuItem className="py-[.4em] px-[.8em]">
          {t('PLAYER_SETTINGS_CAPTIONS')}
          <MediaCaptionsMenu slot="submenu" hidden>
            <div slot="title">{t('PLAYER_SETTINGS_CAPTIONS')}</div>
          </MediaCaptionsMenu>
        </MediaSettingsMenuItem>
        <MediaSettingsMenuItem className="py-[.4em] px-[.8em]">
          {t('PLAYER_SETTINGS_QUALITY')}
          <MediaRenditionMenu slot="submenu" hidden>
            <div slot="title">{t('PLAYER_SETTINGS_QUALITY')}</div>
          </MediaRenditionMenu>
        </MediaSettingsMenuItem>
      </MediaSettingsMenu>
      <MediaControlBar
        className={clsx({ hidden: !initialized || mode === 'preview' })}
      >
        <div
          className={clsx({
            'flex flex-col w-full z-1 leading-normal px-3 py-2.5 gap-(--vertical-spacing-2)': true,
            'bg-(--player-background-color-default)': true,
            'text-(--player-text-color-primary)': true,
          })}
        >
          <div className="flex flex-row items-center justify-center sm:justify-between gap-4">
            <div className="hidden md:flex items-center gap-(--horizontal-spacing-1)">
              <PlayerSeekBackwardButton disabled={!aclCheckInternal(acl)} />
              <PlayAuthButton
                className={clsx({ hidden: aclCheckInternal(acl) })}
                disabled={aclCheckInternal(acl)}
                onClick={() => {
                  playTriggerRef.current = {
                    link_id: 'video-play-auth-button',
                    link_text: 'Play',
                  };
                  aclCheck(true);
                }}
              />
              <div className={clsx({ hidden: !aclCheckInternal(acl) })}>
                {/* MediaPlayButton toggles playback internally on the bubble
                    phase. This capture-phase wrapper lets us intercept the
                    click first, use the cached token, and prevent the native
                    toggle from racing ahead of our token handling. */}
                <span onClickCapture={handlePlayPauseClick}>
                  <MediaPlayButton
                    style={defaultButtonStyling}
                    disabled={!aclCheckInternal(acl)}
                  >
                    <span slot="play">
                      <Button aria-hidden="true" tabIndex={-1} size="sm">
                        <HeroIcon icon={PlayIcon} />
                      </Button>
                    </span>
                    <span slot="pause">
                      <Button aria-hidden="true" tabIndex={-1} size="sm">
                        <HeroIcon icon={PauseIcon} />
                      </Button>
                    </span>
                  </MediaPlayButton>
                </span>
              </div>
              <PlayerSeekForwardButton disabled={!aclCheckInternal(acl)} />
            </div>
            <div className="flex flex-1">
              <div>
                <MediaTimeDisplay
                  className="text-body-sm"
                  style={defaultDisplayStyling}
                  noToggle
                />
              </div>
              <div className="flex grow">
                <MediaTimeRange
                  className={`flex-1 ${styles.MediaRange}`}
                  style={defaultRangeStyling}
                  onMouseDown={() => (isScrubbingRef.current = true)}
                  onMouseUp={() => {
                    isScrubbingRef.current = false;
                    handleOnSeeked();
                  }}
                />
                <div>
                  <span className="font-body font-body-sm text-body-sm font-weight-sm color-(--player-text-color-primary)">
                    {type !== 'vimeo' && durationDisplay}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex sm:gap-2">
              <div className="flex">
                {trackId && (
                  <LikeButton
                    contentId={Number(trackId)}
                    contentType="Video"
                    iconOnly
                  />
                )}
                <div className="hidden md:flex">
                  <MediaMuteButton
                    style={defaultButtonStyling}
                    disabled={!aclCheckInternal(acl)}
                  >
                    <span slot="high">
                      <Button aria-hidden="true" tabIndex={-1} variant="ghost">
                        <HeroIcon icon={SpeakerWaveIcon} />
                      </Button>
                    </span>
                    <span slot="medium">
                      <Button aria-hidden="true" tabIndex={-1} variant="ghost">
                        <HeroIcon icon={SpeakerWaveIcon} />
                      </Button>
                    </span>
                    <span slot="low">
                      <Button aria-hidden="true" tabIndex={-1} variant="ghost">
                        <HeroIcon icon={SpeakerWaveIcon} />
                      </Button>
                    </span>
                    <span slot="off">
                      <Button aria-hidden="true" tabIndex={-1} variant="ghost">
                        <HeroIcon icon={SpeakerXMarkIcon} />
                      </Button>
                    </span>
                  </MediaMuteButton>
                  <MediaVolumeRange
                    className={`hidden sm:flex flex-1 ${styles.MediaRange}`}
                    style={defaultRangeStyling}
                  >
                    <span slot="thumb" />
                  </MediaVolumeRange>
                </div>
              </div>
              <div className="flex">
                {type !== 'file' && (
                  <PlayerCastButton
                    disabled={!aclCheckInternal(acl)}
                    type={type}
                    videoRef={videoRef}
                  />
                )}
                <MediaSettingsMenuButton
                  style={defaultButtonStyling}
                  disabled={!aclCheckInternal(acl)}
                >
                  <span slot="icon">
                    <Button
                      aria-hidden="true"
                      tabIndex={-1}
                      variant="ghost"
                      size="md"
                    >
                      <HeroIcon icon={AdjustmentsHorizontalIcon} />
                    </Button>
                  </span>
                </MediaSettingsMenuButton>
                <MediaFullscreenButton
                  style={defaultButtonStyling}
                  disabled={!aclCheckInternal(acl)}
                >
                  <span slot="enter">
                    <Button
                      aria-hidden="true"
                      tabIndex={-1}
                      variant="ghost"
                      size="md"
                    >
                      <HeroIcon icon={ArrowsPointingOutIcon} />
                    </Button>
                  </span>
                  <span slot="exit">
                    <Button
                      aria-hidden="true"
                      tabIndex={-1}
                      variant="ghost"
                      size="md"
                    >
                      <HeroIcon icon={ArrowsPointingInIcon} />
                    </Button>
                  </span>
                </MediaFullscreenButton>
              </div>
            </div>
          </div>
        </div>
      </MediaControlBar>
    </MediaController>
  );
}

const buildUrlWithQuery = (
  base: string,
  token?: string,
  ...query: string[]
) => {
  const playbackIdQueryParts = [];

  if (token) {
    playbackIdQueryParts.push(`token=${token}`);
  }

  playbackIdQueryParts.push(query);

  const playbackIdQuery = `${playbackIdQueryParts.length > 0 ? `?${playbackIdQueryParts.join('&')}` : ''}`;
  return `${base}${playbackIdQuery}`;
};
